/**
 * Content codec — encryption + compression + chunking for file content (v3)
 *
 * - Files ≤ CDC_MIN_CHUNK (16 KiB): single inline object (compressed + encrypted)
 * - Files > CDC_MIN_CHUNK: CDC chunked → compress each chunk → encrypt → v3 manifest
 * - Content-addressed: identical content → identical ciphertext → identical hash
 * - Write-path dedup: compare chunk hashes via hasObject()
 */

import type { ContentRef } from '../types'
import { splitIntoChunksCDC, CDC_MIN_CHUNK, CDC_TARGET_CHUNK, CDC_MAX_CHUNK } from './cdc'
import { resolveCompression, getDecompressor, DEFAULT_COMPRESSION, type CompressionStrategy } from './compress'
import { canonicalize } from './state'
import { packBlockRadixContent, resolveAllBlocks, type BlockManifestV3 } from './blockManifest'

export const MAX_OBJECT_BYTES = 2 * 1024 * 1024
export const CHUNK_OVERHEAD_BYTES = 24 + 16
export const CHUNK_PLAINTEXT_MAX = MAX_OBJECT_BYTES - CHUNK_OVERHEAD_BYTES

// Re-export CDC constants for consumers
export { CDC_MIN_CHUNK, CDC_TARGET_CHUNK, CDC_MAX_CHUNK } from './cdc'
export { splitIntoChunksCDC } from './cdc'

// ── Manifest type ─────────────────────────────────────────────────

/** v3 manifest (CDC + compression + per-chunk plainSize + totalPlainSize). */
export interface FileManifestV3 {
    type: 'secureblob-file-manifest'
    v: 3
    chunking: string       // 'fastcdc-gear' or 'fixed-page'
    compression: string    // 'deflate-raw', 'none', etc.
    params: {
        minChunk: number
        targetChunk: number
        maxChunk: number
        maskBits: number
    }
    totalPlainSize: number
    plaintextSha256: string | null  // null after chunk merge
    chunks: Array<{ hash: string; plainSize: number }>
}

// ── Codec deps ────────────────────────────────────────────────────

export interface ContentCodecDeps {
    encryptRawObject(plaintext: Uint8Array): Promise<Uint8Array>
    decryptRawObject(objectBytes: Uint8Array): Promise<Uint8Array>
    sha256(data: Uint8Array): Promise<string>
    /** Content-addressed deterministic encryption (v3). */
    encryptDeterministic(plaintext: Uint8Array): Promise<Uint8Array>
    /** Check if object already exists locally (write-path dedup). */
    hasObject(hash: string): Promise<boolean>
}

export interface PackOptions {
    compression?: string   // override compression algorithm
    chunking?: string      // 'fastcdc-gear' (default) or 'block-radix'
    mime?: string           // used for MIME auto-skip of compression
    /** Block size for block-radix chunking (default: 128 for .mdr, 4096 for .db). */
    blockSize?: number
}

export interface PackedFileContent {
    content: ContentRef
    objects: Array<{ hash: string; bytes: Uint8Array }>
}

function assertManifestChunkPlainSize(hash: string, expected: number, actual: number): void {
    if (actual !== expected) {
        throw new Error(`Corrupted manifest chunk ${hash}: plainSize (${expected}) !== actual (${actual})`)
    }
}

// ── Legacy fixed-offset split (kept for reference) ────────────────

export function splitIntoChunks(bytes: Uint8Array, maxChunkSize = CHUNK_PLAINTEXT_MAX): Uint8Array[] {
    if (maxChunkSize <= 0) throw new Error('maxChunkSize must be > 0')
    if (bytes.length === 0) return [new Uint8Array()]

    const chunks: Uint8Array[] = []
    for (let offset = 0; offset < bytes.length; offset += maxChunkSize) {
        chunks.push(bytes.slice(offset, Math.min(bytes.length, offset + maxChunkSize)))
    }
    return chunks
}

/**
 * Encrypt file content into objects using the v3 pipeline.
 *
 * Pipeline: plaintext → CDC → compress each chunk → encrypt (deterministic) → v3 manifest
 *
 * Content-addressed: identical content → identical ciphertext → identical hash.
 * Write-path dedup: unchanged chunks skipped via hasObject().
 */
export async function packFileContent(plaintext: Uint8Array, deps: ContentCodecDeps, opts?: PackOptions): Promise<PackedFileContent> {
    // ── Block-radix branch (§97) ──────────────────────────────────
    if (opts?.chunking === 'block-radix') {
        return packBlockRadixContent(plaintext, deps, opts.blockSize ?? 128, opts)
    }

    const compressor = resolveCompression(opts)

    // Compute plaintext SHA-256 for integrity
    const plaintextSha256Hex = await deps.sha256(plaintext)

    // ── Small files → inline ──────────────────────────────────────────
    if (plaintext.length <= CDC_MIN_CHUNK) {
        const compressed = compressor.compress(plaintext)
        const objectBytes = await deps.encryptDeterministic(compressed)
        const hash = await deps.sha256(objectBytes)

        // Write-path dedup
        if (await deps.hasObject(hash)) {
            return {
                content: { type: 'inline', hash, compression: compressor.id },
                objects: [],
            }
        }

        return {
            content: { type: 'inline', hash, compression: compressor.id },
            objects: [{ hash, bytes: objectBytes }],
        }
    }

    // ── Large files → CDC chunking ──────────────────────────────────
    const cdcChunks = splitIntoChunksCDC(plaintext)
    const chunkEntries: Array<{ hash: string; plainSize: number }> = []
    const objects: Array<{ hash: string; bytes: Uint8Array }> = []

    for (const chunk of cdcChunks) {
        const compressed = compressor.compress(chunk)
        const chunkObject = await deps.encryptDeterministic(compressed)
        const chunkHash = await deps.sha256(chunkObject)

        chunkEntries.push({ hash: chunkHash, plainSize: chunk.length })

        // Write-path dedup: skip if chunk already exists locally
        if (await deps.hasObject(chunkHash)) continue
        objects.push({ hash: chunkHash, bytes: chunkObject })
    }

    // ── v3 manifest ───────────────────────────────────────────────
    const manifest: FileManifestV3 = {
        type: 'secureblob-file-manifest',
        v: 3,
        chunking: 'fastcdc-gear',
        compression: compressor.id,
        params: {
            minChunk: CDC_MIN_CHUNK,
            targetChunk: CDC_TARGET_CHUNK,
            maxChunk: CDC_MAX_CHUNK,
            maskBits: 16,
        },
        totalPlainSize: plaintext.length,
        plaintextSha256: plaintextSha256Hex,
        chunks: chunkEntries,
    }

    // Manifest encrypted deterministically too (same manifest → same hash)
    const manifestCanonical = new TextEncoder().encode(canonicalize(manifest))
    const manifestObject = await deps.encryptDeterministic(manifestCanonical)
    const manifestHash = await deps.sha256(manifestObject)

    // Dedup: skip manifest upload if already exists
    if (!(await deps.hasObject(manifestHash))) {
        objects.push({ hash: manifestHash, bytes: manifestObject })
    }

    return {
        content: { type: 'chunked', manifestHash },
        objects,
    }
}

// ── Resolve (read path) ───────────────────────────────────────────

export interface ContentLoader {
    load(hash: string): Promise<Uint8Array | null>
    loadBatch(hashes: string[]): Promise<Map<string, Uint8Array>>
}

/**
 * Resolve encrypted content ref into plaintext bytes.
 * v3: decompresses each chunk using the manifest's compression field.
 */
export async function resolveContentBytes(
    content: ContentRef,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): Promise<Uint8Array | null> {
    if (content.type === 'inline') {
        const objectBytes = await loader.load(content.hash)
        if (!objectBytes) return null
        const decrypted = await deps.decryptRawObject(objectBytes)
        // Decompress (compression metadata always present in v3)
        if (content.compression) {
            return getDecompressor(content.compression).decompress(decrypted)
        }
        return decrypted
    }

    const manifestObject = await loader.load(content.manifestHash)
    if (!manifestObject) return null

    const manifestBytes = await deps.decryptRawObject(manifestObject)
    const manifest = JSON.parse(new TextDecoder().decode(manifestBytes))

    // ── Block-radix branch (§97) ──────────────────────────────────
    if (manifest.chunking === 'block-radix') {
        return resolveAllBlocks(manifest as BlockManifestV3, loader, deps)
    }

    const cdcManifest = manifest as FileManifestV3

    const chunkHashes = cdcManifest.chunks.map(c => c.hash)
    const chunksData = await loader.loadBatch(chunkHashes)

    for (const hash of chunkHashes) {
        if (!chunksData.has(hash)) return null
    }

    const decompressor = getDecompressor(cdcManifest.compression)

    const combined: Uint8Array[] = []
    for (const chunk of cdcManifest.chunks) {
        const chunkObject = chunksData.get(chunk.hash)!
        const decrypted = await deps.decryptRawObject(chunkObject)
        const plain = decompressor.decompress(decrypted)
        assertManifestChunkPlainSize(chunk.hash, chunk.plainSize, plain.length)
        combined.push(plain)
    }

    // Validate totalPlainSize (poisoned manifest protection)
    const total = combined.reduce((sum, c) => sum + c.length, 0)
    if (total !== cdcManifest.totalPlainSize) {
        throw new Error(`Corrupted manifest: totalPlainSize (${cdcManifest.totalPlainSize}) !== actual (${total})`)
    }

    const out = new Uint8Array(total)
    let offset = 0
    for (const part of combined) {
        out.set(part, offset)
        offset += part.length
    }
    return out
}

/**
 * Stream content bytes chunk-by-chunk.
 * Yields one Uint8Array per decrypted + decompressed chunk — no full-file RAM allocation.
 */
export async function* resolveContentStream(
    content: ContentRef,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): AsyncGenerator<Uint8Array> {
    if (content.type === 'inline') {
        const objectBytes = await loader.load(content.hash)
        if (!objectBytes) return
        const decrypted = await deps.decryptRawObject(objectBytes)
        yield content.compression
            ? getDecompressor(content.compression).decompress(decrypted)
            : decrypted
        return
    }

    const manifestObject = await loader.load(content.manifestHash)
    if (!manifestObject) return

    const manifestBytes = await deps.decryptRawObject(manifestObject)
    const manifest = JSON.parse(new TextDecoder().decode(manifestBytes))

    // ── Block-radix branch (§97) ──────────────────────────────────
    if (manifest.chunking === 'block-radix') {
        const blockManifest = manifest as BlockManifestV3
        const decompressor = getDecompressor(blockManifest.compression)
        for (const hash of blockManifest.children) {
            if (hash === null) {
                yield new Uint8Array(blockManifest.params.blockSize)
                continue
            }
            const encrypted = await loader.load(hash)
            if (!encrypted) {
                throw new Error(`Missing block-radix content object: ${hash}`)
            }
            const decrypted = await deps.decryptRawObject(encrypted)
            yield decompressor.decompress(decrypted)
        }
        return
    }

    const cdcManifest = manifest as FileManifestV3
    const decompressor = getDecompressor(cdcManifest.compression)

    // Stream chunk-by-chunk — only 1 chunk in RAM at a time
    for (const chunk of cdcManifest.chunks) {
        const chunkObject = await loader.load(chunk.hash)
        if (!chunkObject) {
            throw new Error(`Missing chunked content object: ${chunk.hash}`)
        }
        const decrypted = await deps.decryptRawObject(chunkObject)
        const plain = decompressor.decompress(decrypted)
        assertManifestChunkPlainSize(chunk.hash, chunk.plainSize, plain.length)
        yield plain
    }
}

async function loadBlockRadixRangeBlock(
    manifest: BlockManifestV3,
    blockIndex: number,
    decompressor: CompressionStrategy,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): Promise<Uint8Array | null> {
    const hash = manifest.children[blockIndex]
    if (hash === null || hash === undefined) {
        return new Uint8Array(manifest.params.blockSize)
    }
    if (typeof hash !== 'string' || hash.length === 0) {
        return null
    }

    const encrypted = await loader.load(hash)
    if (!encrypted) return null
    const decrypted = await deps.decryptRawObject(encrypted)
    return decompressor.decompress(decrypted)
}

// ── Random Access (v3 fseek/pread) ────────────────────────────────

/**
 * Read a byte range from a file without downloading everything.
 *
 * Uses v3 per-chunk `plainSize` to:
 *   1. Compute which chunks overlap [offset, offset+length)
 *   2. Fetch/decrypt/decompress ONLY those chunks
 *   3. Slice out the requested range
 */
export async function resolveContentRange(
    content: ContentRef,
    offset: number,
    length: number,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): Promise<Uint8Array | null> {
    // Inline files: just fetch, decompress, and slice
    if (content.type === 'inline') {
        const full = await resolveContentBytes(content, loader, deps)
        if (!full) return null
        return full.slice(offset, offset + length)
    }

    const manifestObject = await loader.load(content.manifestHash)
    if (!manifestObject) return null

    const manifestBytes = await deps.decryptRawObject(manifestObject)
    const manifest = JSON.parse(new TextDecoder().decode(manifestBytes))

    if (manifest.chunking === 'block-radix') {
        const blockManifest = manifest as BlockManifestV3
        const blockSize = blockManifest.params.blockSize
        if (blockSize <= 0 || length <= 0) return new Uint8Array(0)

        const maxEnd = blockManifest.children.length * blockSize
        if (offset >= maxEnd) return new Uint8Array(0)

        const rangeEnd = Math.min(offset + length, maxEnd)
        const startBlock = Math.floor(offset / blockSize)
        const endBlock = Math.ceil(rangeEnd / blockSize)
        const decompressor = getDecompressor(blockManifest.compression ?? DEFAULT_COMPRESSION)
        const result = new Uint8Array(rangeEnd - offset)
        let writePos = 0

        for (let blockIndex = startBlock; blockIndex < endBlock; blockIndex++) {
            const plain = await loadBlockRadixRangeBlock(blockManifest, blockIndex, decompressor, loader, deps)
            if (!plain) return null

            const blockStart = blockIndex * blockSize
            const sliceStart = Math.max(0, offset - blockStart)
            const sliceEnd = Math.min(plain.length, rangeEnd - blockStart)
            if (sliceEnd <= sliceStart) continue

            const slice = plain.slice(sliceStart, sliceEnd)
            result.set(slice, writePos)
            writePos += slice.length
        }

        return result.slice(0, writePos)
    }

    const cdcManifest = manifest as FileManifestV3
    const decompressor = getDecompressor(cdcManifest.compression)

    // Build cumulative offset table
    let cumOffset = 0
    const chunkRanges: Array<{ hash: string; start: number; end: number }> = []
    for (const chunk of cdcManifest.chunks) {
        chunkRanges.push({ hash: chunk.hash, start: cumOffset, end: cumOffset + chunk.plainSize })
        cumOffset += chunk.plainSize
    }

    // Find overlapping chunks
    const rangeEnd = Math.min(offset + length, cdcManifest.totalPlainSize)
    const overlapping = chunkRanges.filter(c => c.start < rangeEnd && c.end > offset)

    if (overlapping.length === 0) return new Uint8Array(0)

    // Fetch only needed chunks (batch)
    const neededHashes = overlapping.map(c => c.hash)
    const chunksData = await loader.loadBatch(neededHashes)

    // Assemble result
    const result = new Uint8Array(rangeEnd - offset)
    let writePos = 0

    for (const chunk of overlapping) {
        const chunkObject = chunksData.get(chunk.hash)
        if (!chunkObject) return null

        const decrypted = await deps.decryptRawObject(chunkObject)
        const plain = decompressor.decompress(decrypted)
        assertManifestChunkPlainSize(chunk.hash, chunk.end - chunk.start, plain.length)

        // Calculate slice within this chunk
        const chunkSliceStart = Math.max(0, offset - chunk.start)
        const chunkSliceEnd = Math.min(plain.length, rangeEnd - chunk.start)
        const slice = plain.slice(chunkSliceStart, chunkSliceEnd)

        result.set(slice, writePos)
        writePos += slice.length
    }

    return result.slice(0, writePos)
}

/**
 * Stream content from an arbitrary byte offset with optional length limit.
 *
 * V3 optimization: uses per-chunk `plainSize` to seek past earlier chunks in O(1).
 * Only decrypts chunks that overlap the requested range [offset, offset+length).
 * For the first overlapping chunk, slices from the correct byte position.
 *
 * Use cases:
 *   - FUSE read() with offset — stream from any position
 *   - HTTP Range responses — seek then stream
 *   - Seekable media playback — skip to any position
 *   - Resumable downloads — continue from byte N
 */
export async function* resolveContentStreamRange(
    content: ContentRef,
    offset: number,
    length: number | undefined,
    loader: ContentLoader,
    deps: ContentCodecDeps,
): AsyncGenerator<Uint8Array> {
    // Inline files: just fetch, decompress, slice, yield
    if (content.type === 'inline') {
        const full = await resolveContentBytes(content, loader, deps)
        if (!full) return
        const end = length !== undefined ? Math.min(offset + length, full.length) : full.length
        if (offset >= full.length) return
        yield full.slice(offset, end)
        return
    }

    const manifestObject = await loader.load(content.manifestHash)
    if (!manifestObject) return

    const manifestBytes = await deps.decryptRawObject(manifestObject)
    const manifest = JSON.parse(new TextDecoder().decode(manifestBytes))

    if (manifest.chunking === 'block-radix') {
        const blockManifest = manifest as BlockManifestV3
        const blockSize = blockManifest.params.blockSize
        if (blockSize <= 0) return

        const maxEnd = blockManifest.children.length * blockSize
        if (offset >= maxEnd) return

        const rangeEnd = length !== undefined
            ? Math.min(offset + length, maxEnd)
            : maxEnd
        const startBlock = Math.floor(offset / blockSize)
        const endBlock = Math.ceil(rangeEnd / blockSize)
        const decompressor = getDecompressor(blockManifest.compression ?? DEFAULT_COMPRESSION)
        let bytesYielded = 0

        for (let blockIndex = startBlock; blockIndex < endBlock; blockIndex++) {
            const plain = await loadBlockRadixRangeBlock(blockManifest, blockIndex, decompressor, loader, deps)
            if (!plain) {
                throw new Error(`Missing block-radix content object at block ${blockIndex}`)
            }

            const blockStart = blockIndex * blockSize
            const sliceStart = Math.max(0, offset - blockStart)
            const sliceEnd = Math.min(plain.length, rangeEnd - blockStart)
            if (sliceEnd <= sliceStart) continue

            const slice = plain.slice(sliceStart, sliceEnd)
            yield slice
            bytesYielded += slice.length

            if (length !== undefined && bytesYielded >= length) break
        }
        return
    }

    const cdcManifest = manifest as FileManifestV3
    const decompressor = getDecompressor(cdcManifest.compression)

    // Build cumulative offset table for O(1) seek
    let cumOffset = 0
    const chunkRanges: Array<{ hash: string; start: number; end: number; plainSize: number }> = []
    for (const chunk of cdcManifest.chunks) {
        chunkRanges.push({ hash: chunk.hash, start: cumOffset, end: cumOffset + chunk.plainSize, plainSize: chunk.plainSize })
        cumOffset += chunk.plainSize
    }

    const rangeEnd = length !== undefined
        ? Math.min(offset + length, cdcManifest.totalPlainSize)
        : cdcManifest.totalPlainSize

    // Stream only overlapping chunks
    let bytesYielded = 0
    for (const chunk of chunkRanges) {
        // Skip chunks entirely before the offset
        if (chunk.end <= offset) continue
        // Stop if we've passed the range
        if (chunk.start >= rangeEnd) break

        const chunkObject = await loader.load(chunk.hash)
        if (!chunkObject) {
            throw new Error(`Missing chunked content object: ${chunk.hash}`)
        }
        const decrypted = await deps.decryptRawObject(chunkObject)
        const plain = decompressor.decompress(decrypted)
        assertManifestChunkPlainSize(chunk.hash, chunk.plainSize, plain.length)

        // Calculate slice within this chunk
        const sliceStart = Math.max(0, offset - chunk.start)
        const sliceEnd = Math.min(plain.length, rangeEnd - chunk.start)
        const slice = sliceStart > 0 || sliceEnd < plain.length
            ? plain.slice(sliceStart, sliceEnd)
            : plain

        yield slice
        bytesYielded += slice.length

        if (length !== undefined && bytesYielded >= length) break
    }
}
